import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { Subject, Subscription } from 'rxjs';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { ReportsService } from 'src/app/metrics/services/reports.service';
import { SftpServer } from 'src/app/parameters/sftp-servers/models/sftp-server.model';
import { SftpServersService } from 'src/app/parameters/sftp-servers/sftp-servers.service';
import { UserV2 } from 'src/app/parameters/users/shared/models';
import { Choice } from 'src/app/shared/components/select/choice.model';
import { BasicService } from 'src/app/shared/services/basic.service';
import { Report } from '../../../models';
import { ReportEditionMetricDialogComponent } from '../report-edition-metric-dialog/report-edition-metric-dialog.component';
import { ReportEditionVerbatimDialogComponent } from '../report-edition-verbatim-dialog/report-edition-verbatim-dialog.component';

@Component({
  selector: 'app-report-edition-form',
  templateUrl: './report-edition-form.component.html',
  styleUrls: ['./report-edition-form.component.scss'],
})
export class ReportEditionFormComponent implements OnInit, OnDestroy {
  @Input() report: Report;
  @Input() isCreation: boolean;
  @Input() clickedSave: Subject<void>;
  @Output() formValid = new EventEmitter<boolean>();

  periodicities: any; // cannot add type because the reactive form are not strongly typed - TODO Migration Angular 14
  isAdmin: number;
  sftpServers: SftpServer[];
  users: UserV2[];
  nonBotUsers: UserV2[];

  sftpServersItems: Choice[];
  nonBotUsersItems: Choice[];

  status: boolean;

  form: FormGroup = this.formBuilder.group({
    name: ['', Validators.required],
    dateFieldToVary: [''],
    periodicity: ['', Validators.required],
    email: [''],
    ftp: [''],
    ftpConfigId: [''],
    onlyForMe: [''],
    createdForUsers: [''],
  });

  private subscriptions = new Subscription();

  constructor(
    private basicService: BasicService,
    private formBuilder: FormBuilder,
    private reportsService: ReportsService,
    private alcmeonContextService: AlcmeonContextService,
    private sftpServersService: SftpServersService,
    public dialogRef: MatDialogRef<ReportEditionMetricDialogComponent | ReportEditionVerbatimDialogComponent>
  ) {}

  /**
   * This form is for two types of report -> metric report and verbatim
   * In verbatim we do not show varying criterion (dateFieldToVary)
   * Validations: name and periodicity always required
   * dateFieldToVary required only for metric report
   * ftpConfigId required only when ftp = true (checkbox) (only if user is admin)
   * createdForUsers required when onlyForMe = false (checkbox)
   */
  ngOnInit(): void {
    this.isAdmin = this.alcmeonContextService.isLoggedUserAdmin();
    this.periodicities = this.reportsService.getPeriodicityFrontEndToBackend();
    this.initFrontendPeriodicity(this.report);
    this.report.ui.onlyForMe =
      this.report.created_for_users.length === 1 &&
      this.report.created_for_users[0] === this.alcmeonContextService.self.id;
    this.users = this.alcmeonContextService.getCompany().users;
    this.nonBotUsers = this.users.filter(user => !user.is_bot && user.is_active);
    this.nonBotUsersItems = this.nonBotUsers.map(nonBotUser => ({ id: nonBotUser.id, label: nonBotUser.realname }));

    this.initFormValues();

    this.setCreatedForUsersValidators(this.report.ui.onlyForMe);

    this.formValid.emit(this.form.valid);
    this.status = this.form.valid;

    this.subscriptions.add(
      this.form.statusChanges.subscribe(() => {
        if (!this.status === this.form.valid) {
          this.status = this.form.valid;
          this.formValid.emit(this.form.valid);
        }
      })
    );

    this.subscriptions.add(
      this.form.get('ftp').valueChanges.subscribe(ftp => {
        this.setFTPValidators(ftp);
      })
    );

    this.subscriptions.add(
      this.form.get('onlyForMe').valueChanges.subscribe(onlyForMe => {
        this.setCreatedForUsersValidators(onlyForMe);
      })
    );

    this.subscriptions.add(
      this.clickedSave.subscribe(() => {
        this.save();
      })
    );
  }

  private initFormValues() {
    // if user is not an admin -> we do not show checkbox for ftp
    if (this.isAdmin) {
      this.sftpServersService.getSftpServersList().subscribe((sftpServers: SftpServer[]) => {
        this.sftpServersItems = sftpServers.map(sftpServer => ({ id: sftpServer.id, label: sftpServer.name }));
        if (this.sftpServersItems.length) {
          this.form.get('ftp').setValue(this.report.send_by.ftp);

          if (this.report.send_by.ftp) {
            this.form.get('ftpConfigId').setValue(this.report.send_by.ftp_config_id);
          }
        } else {
          this.form.get('ftp').disable();
        }
      });

      this.setFTPValidators(this.report.send_by.ftp);
    }

    this.form.get('name').setValue(this.report.name);

    this.form.get('periodicity').setValue(this.report.ui.periodicity);

    this.form.get('email').setValue(this.report.send_by.email);

    this.form.get('onlyForMe').setValue(this.report.ui.onlyForMe);

    this.form.get('createdForUsers').setValue(this.report.created_for_users);

    if (this.report.metric_id) {
      this.form.get('dateFieldToVary').setValue(this.report.date_field_to_vary);
      this.form.get('dateFieldToVary').addValidators([Validators.required]);
    }
  }

  /**
   * Initializes periodicity for the form
   */
  private initFrontendPeriodicity(report: Report) {
    this.periodicities.forEach(periodicity => {
      if (periodicity && periodicity.when === report.when && periodicity.duration === report.duration) {
        report.ui.periodicity = periodicity;
      }
    });
  }

  private setFTPValidators(ftp: boolean) {
    if (ftp) {
      this.form.get('ftpConfigId').addValidators(Validators.required);
    } else {
      this.form.get('ftpConfigId').removeValidators(Validators.required);
      this.form.get('ftpConfigId').setErrors(null);
    }
    this.form.get('ftpConfigId').updateValueAndValidity({ onlySelf: true });
  }

  private setCreatedForUsersValidators(onlyForMe: boolean) {
    if (onlyForMe) {
      this.form.get('createdForUsers').removeValidators(Validators.required);
      this.form.get('createdForUsers').setErrors(null);
    } else {
      this.form.get('createdForUsers').addValidators(Validators.required);
    }
    this.form.get('createdForUsers').updateValueAndValidity({ onlySelf: true });
  }

  save() {
    this.report.name = this.form.value.name;
    this.report.date_field_to_vary = this.form.value.dateFieldToVary;
    this.report.when = this.form.value.periodicity.when;
    this.report.duration = this.form.value.periodicity.duration;
    this.report.send_by.email = this.form.value.email;
    //FTP
    if (this.isAdmin) {
      this.report.send_by.ftp = this.form.value.ftp;
      if (this.form.value.ftp) {
        this.report.send_by.ftp_config_id = this.form.value.ftpConfigId;
      } else {
        this.report.send_by.ftp = false;
      }
    }
    //ONLY FOR ME
    if (this.form.value.onlyForMe) {
      this.report.created_for_users = [this.alcmeonContextService.self.id];
    } else {
      this.report.created_for_users = this.form.value.createdForUsers;
    }

    const reportCopy = { ...this.report };
    delete reportCopy.ui;

    if (this.isCreation) {
      const creation = this.report.metric_id
        ? this.reportsService.createMetricReport(reportCopy)
        : this.reportsService.createVerbatimReport(reportCopy);
      const message = this.report.metric_id
        ? 'metric-reports/edition/create-success'
        : 'verbatim-reports/edition/create-success';

      creation.subscribe({
        complete: () => this.basicService.toastSuccess(message),
        next: () => {
          this.dialogRef.close();
        },
      });
    } else {
      const edition = this.report.metric_id
        ? this.reportsService.updateMetricReport(reportCopy)
        : this.reportsService.updateVerbatimReport(reportCopy);

      const message = this.report.metric_id
        ? 'metric-reports/edition/edit-success'
        : 'verbatim-reports/edition/edit-success';

      edition.subscribe({
        complete: () => this.basicService.toastSuccess(message),
        next: () => {
          this.dialogRef.close();
        },
      });
    }
  }

  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }
}
